import { normalizeLedgerFields } from "./ledger-field-compat";
import type { ExpenseInput, Owner } from "../types";
import type { BatchItem } from "../features/entry/batch-save";

const PREFIX = "barnyard-pending-entry-";
export const PENDING_CHANGED = "barnyard-pending-changed";
export type PendingEntry = ExpenseInput & {
  owner: Owner;
  progress?: BatchItem;
  recurring?: Record<string, unknown>;
};

export function pendingChanged() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(PENDING_CHANGED));
}

export function readPendingEntries(): { key: string; entry: PendingEntry }[] {
  const entries: { key: string; entry: PendingEntry }[] = [];
  const keys = new Set<string>();
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(PREFIX)) keys.add(key);
  }
  for (const owner of ["Bun", "Chicken"] as Owner[]) keys.add(`barnyard-pending-expense-${owner}`);
  for (const key of keys) {
    try {
      const value = localStorage.getItem(key) || (typeof sessionStorage !== "undefined" ? sessionStorage.getItem(key) : null);
      const entry = normalizeLedgerFields(JSON.parse(value || "null")) as PendingEntry | null;
      if (!entry?.requestId || !entry.accountId || !entry.amount || !entry.date) continue;
      const owner = entry.owner || (key.endsWith("Chicken") ? "Chicken" : "Bun");
      entries.push({ key, entry: { ...entry, owner } });
    } catch { /* Ignore malformed records, without deleting them. */ }
  }
  return entries;
}

export function enqueuePendingEntry(entry: PendingEntry) {
  const key = PREFIX + entry.requestId;
  // Never overwrite progress already recorded for this request.
  if (!localStorage.getItem(key)) localStorage.setItem(key, JSON.stringify(entry));
  pendingChanged();
  return key;
}

export function migratePendingEntries() {
  for (const { key, entry } of readPendingEntries()) {
    if (key.startsWith(PREFIX)) continue;
    enqueuePendingEntry(entry); // Persist successfully before removing the old single-entry slot.
    localStorage.removeItem(key);
    if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(key);
  }
}

export function canChangePending(key: string, entry: PendingEntry) {
  return key.startsWith(PREFIX) && (!entry.progress || (
    entry.progress.status === "error" && entry.progress.validationRejected === true &&
    !entry.progress.transactionId && !entry.progress.savedPriceIds?.length
  ));
}

/** Recheck inside the dispatch lock: an entry may have started syncing since its editor opened. */
export async function changePendingEntry(key: string, original: PendingEntry, expense: ExpenseInput | null, currency?: string) {
  const change = () => {
    const current = readPendingEntries().find(item => item.key === key)?.entry;
    if (!current) throw new Error("This entry has synced. Reopen it from Transactions.");
    if (!canChangePending(key, current)) throw new Error("This entry has already been sent. Wait for sync confirmation before editing or deleting it.");
    if (JSON.stringify(current) !== JSON.stringify(original)) throw new Error("This entry changed in another tab. Close and reopen it before editing.");
    if (expense) {
      const recurring = current.recurring ? {
        ...current.recurring, type: expense.type === "Income" ? "Income" : "Expense",
        name: expense.title || expense.category, accountId: expense.accountId,
        amount: expense.amount, category: expense.category, subcategory: expense.subcategory,
        payeeId: expense.payeeId,
        currency: currency || current.recurring.currency,
      } : undefined;
      localStorage.setItem(key, JSON.stringify({
        ...expense,
        requestId: current.progress ? crypto.randomUUID() : current.requestId,
        owner: current.owner,
        recurring,
        newCategory: expense.newCategory,
        newSubcategory: expense.newSubcategory,
      }));
    } else localStorage.removeItem(key);
    pendingChanged();
  };
  if (typeof navigator !== "undefined" && navigator.locks) await navigator.locks.request("barnyard-pending-sync", change);
  else change();
}
